import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
} from "@/api/attachment";
import { useGetSculptureEditionById } from "@/api/sculpture-edition/sculpture-edition";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getDefaultImage } from "@/lib/image";
import { cn } from "@/lib/utils";
import { ImageOff } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HtmlCell } from "../table/CustomCell";

type SculptureEditionPreviewSheetProps = {
  editionId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const SculptureEditionPreviewSheet = ({
  editionId,
  open,
  onOpenChange,
}: SculptureEditionPreviewSheetProps) => {
  const { t } = useTranslation();
  const { data: edition, isLoading } = useGetSculptureEditionById(
    editionId ?? "",
  );
  const { data: attachments, isLoading: attachmentsLoading } =
    useGetAttachmentsByDocId(editionId ?? "");

  const previewAttachments = useMemo(() => {
    if (!attachments?.length) return [];
    const status =
      edition?.sculptureEditionPublicationStatus ?? "draft";
    return attachments.filter((a) =>
      shouldIncludeAttachment(a, status as "published" | "draft"),
    );
  }, [attachments, edition?.sculptureEditionPublicationStatus]);

  const previewImage = useMemo(() => {
    if (!previewAttachments.length) return undefined;
    return getDefaultImage(previewAttachments, "sculptureEdition");
  }, [previewAttachments]);

  const subtitle = useMemo(() => {
    const num = edition?.sculptureEditionNumeroEnregistrement?.trim();
    return num || null;
  }, [edition?.sculptureEditionNumeroEnregistrement]);

  const showSkeleton = isLoading && !edition;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-lg w-full flex flex-col gap-0 border-l border-border/80 bg-gradient-to-b from-slate-50/90 to-white p-0 overflow-hidden"
      >
        <SheetHeader className="space-y-1 px-6 pt-6 pb-4 text-left border-b border-border/60 bg-white/80 shrink-0">
          <SheetDescription className="sr-only">
            {t("sculpture.form.view")}
          </SheetDescription>
          <SheetTitle className="text-xl font-semibold leading-snug text-slate-900 pr-10">
            {subtitle ? subtitle : null}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-8">
          <section className="space-y-3">
            {showSkeleton || attachmentsLoading ? (
              <div
                className={cn(
                  "relative aspect-[4/3] w-full rounded-2xl border border-dashed border-slate-200/80",
                  "bg-slate-100/80 animate-pulse",
                )}
                aria-hidden
              />
            ) : previewImage ? (
              <div className=" overflow-hidden">
                <div className=" w-full flex items-center justify-center ">
                  <img
                    src={previewImage}
                    alt=""
                    className=" w-full object-contain"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-14 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200/60 text-slate-500">
                  <ImageOff className="h-6 w-6" aria-hidden />
                </div>
                <p className="text-xs text-muted-foreground">—</p>
              </div>
            )}
          </section>

          <Separator className="bg-border/70" />

          <section className="space-y-3 pb-2">
            <h3 className="text-sm font-semibold text-slate-800">
              {t("sculpture.form.sculptureEditionNotes")}
            </h3>
            {isLoading && !edition ? (
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-slate-100 animate-pulse" />
                <div className="h-3 w-[92%] rounded bg-slate-100 animate-pulse" />
                <div className="h-3 w-4/5 rounded bg-slate-100 animate-pulse" />
              </div>
            ) : (
              <div
                className={cn(
                  "rounded-xl border border-slate-200/80 bg-white/90 px-4 py-3.5 text-sm leading-relaxed text-slate-700",
                  "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.7)]",
                )}
              >
                {edition?.sculptureEditionNotes?.trim() ? (
                  <HtmlCell value={edition.sculptureEditionNotes} />
                ) : (
                  <p className="text-muted-foreground italic">—</p>
                )}
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SculptureEditionPreviewSheet;
